/* Locaties (/crm/sites): één compacte, doorzoekbare lijst, zoals de projectlijst.
 *
 *   /           zoeken
 *   ↑ ↓ (j k)   door de lijst
 *   Enter       locatie openen (cmd-klik op een rij: nieuw tabblad)
 *
 * Tabs: alle, actief, met monitoring, inactief. Per locatie: nummer, naam, project en
 * plaats, type, status, en bij energiemonitoring de stand van nu (verbruik, net,
 * batterij) met de tijd van de laatste meting. Naast de lijst een kaartweergave.
 *
 * Welke locaties monitoring hebben komt uit één vraag voor de hele lijst (F9.12); zonder
 * zekerheid daarover kan niets verwijderd worden (VerwijderLocatie). */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowDown, ArrowUp, Gauge, List, Map as KaartIcoon, Pencil, Plus, Search, Share } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { LiveLocatie } from '../dashboard/data/dashboard';
import {
  bewaarLijst, filterEnSorteer, filterNaarUrl, geleden, plaatsUit, tellingen, TAB_LABEL,
  type LijstFilter, type LijstLocatie, type Sortering, type Tab,
} from './data/lijst';
import { siteStatusLabel, siteTypeLabel } from './data/labels';
import type { EmsKenmerk } from './data/verwijderen';
import { VerwijderLocatie } from './ui/VerwijderLocatie';

const STATUS_KLEUR: Record<string, string> = {
  active: 'bg-green-50 text-green-800 border-green-200',
  maintenance: 'bg-amber-50 text-amber-800 border-amber-200',
  inactive: 'bg-zinc-100 text-zinc-600 border-zinc-200',
};
const STIP: Partial<Record<Tab, string>> = { actief: 'bg-green-600', monitoring: 'bg-emerald-500', inactief: 'bg-zinc-400' };

const KW = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 });
const kw = (v: number | null) => (v === null ? '—' : `${KW.format(v)} kW`);

/** Een meting ouder dan een uur is niet meer "live": de ophaaldienst draait elke 5 minuten. */
const isVers = (iso: string | null, nu: Date = new Date()) => Boolean(iso) && nu.getTime() - new Date(iso!).getTime() < 60 * 60_000;

function Monitoring({ kenmerk, live }: { kenmerk: EmsKenmerk; live: LiveLocatie | undefined }) {
  if (kenmerk.soort !== 'met-ems') return <span className="text-muted-foreground">{kenmerk.soort === 'laden' ? '…' : '—'}</span>;
  const vers = isVers(live?.laatste ?? null);
  const delen = live
    ? [live.netKw !== null ? `net ${kw(live.netKw)}` : null, live.batterijProcent !== null ? `batt. ${Math.round(live.batterijProcent)}%` : null].filter(Boolean)
    : [];
  return (
    <span data-kenmerk="energiemonitoring" className="flex min-w-0 flex-col">
      <span className="flex items-center gap-1.5 font-medium">
        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', vers ? 'bg-emerald-500' : 'bg-amber-500')} aria-hidden />
        {live?.verbruikKw != null ? kw(live.verbruikKw) : 'Monitoring'}
        {live?.laatste && <span className="font-normal text-muted-foreground" title={live.laatste}>· {geleden(live.laatste)}</span>}
      </span>
      {delen.length > 0 && <span className="truncate text-xs text-muted-foreground">{delen.join(' · ')}</span>}
    </span>
  );
}

const KOLOMMEN =
  'grid-cols-[48px_minmax(0,1fr)] sm:grid-cols-[56px_minmax(0,1fr)_104px] md:grid-cols-[56px_minmax(0,1fr)_110px_96px] lg:grid-cols-[60px_minmax(0,1fr)_120px_100px_200px]';

interface Props {
  locaties: LijstLocatie[];
  laden: boolean;
  fout: Error | null;
  metEms: Set<string>;
  emsFout: string | null;
  kenmerkVoor: (id: string) => EmsKenmerk;
  live: Map<string, LiveLocatie>;
  filter: LijstFilter;
  onFilter: (f: LijstFilter) => void;
  magBeheren: boolean;
  onNieuw: () => void;
  onBewerk: (l: LijstLocatie) => void;
  onVerwijder: (l: LijstLocatie) => void;
  /** De kaart met pinnen voor de kaartweergave (Leaflet of Google, via de pagina). */
  kaart: (locaties: LijstLocatie[]) => ReactNode;
}

export function LocatieLijst(props: Props) {
  const { locaties, laden, fout, metEms, filter, onFilter } = props;
  const navigate = useNavigate();
  const zoekRef = useRef<HTMLInputElement>(null);
  const rijRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [actief, setActief] = useState(-1);

  const zichtbaar = useMemo(() => filterEnSorteer(locaties, filter, metEms), [locaties, filter, metEms]);
  const tabs = useMemo(() => tellingen(locaties, filter.zoek, metEms), [locaties, filter.zoek, metEms]);

  useEffect(() => setActief(-1), [filter.zoek, filter.tab]);
  useEffect(() => {
    if (actief >= 0) rijRefs.current[actief]?.scrollIntoView({ block: 'nearest' });
  }, [actief]);
  useEffect(() => {
    if (!laden) bewaarLijst(new URLSearchParams(filterNaarUrl(filter)).toString(), zichtbaar.map((l) => l.id));
  }, [zichtbaar, laden, filter]);

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      const doel = e.target as HTMLElement;
      const typen = doel.tagName === 'INPUT' || doel.tagName === 'TEXTAREA' || doel.isContentEditable;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!typen && e.key === '/') {
        e.preventDefault();
        zoekRef.current?.focus();
        return;
      }
      if (typen && doel !== zoekRef.current) return;
      if (filter.weergave !== 'lijst') return;
      if (e.key === 'ArrowDown' || (!typen && e.key === 'j')) {
        e.preventDefault();
        if (typen) zoekRef.current?.blur();
        setActief((a) => Math.min(zichtbaar.length - 1, a + 1));
      } else if (e.key === 'ArrowUp' || (!typen && e.key === 'k')) {
        e.preventDefault();
        setActief((a) => Math.max(0, a - 1));
      } else if (e.key === 'Enter' && zichtbaar.length) {
        const l = zichtbaar[actief >= 0 ? actief : 0];
        if (l && (typen || actief >= 0)) {
          e.preventDefault();
          navigate(`/crm/sites/${l.id}`);
        }
      } else if (e.key === 'Escape' && typen) {
        onFilter({ ...filter, zoek: '' });
        zoekRef.current?.blur();
      }
    };
    window.addEventListener('keydown', toets);
    return () => window.removeEventListener('keydown', toets);
  }, [zichtbaar, actief, filter, onFilter, navigate]);

  const sorteer = (s: Sortering) =>
    onFilter({ ...filter, sortering: s, oplopend: filter.sortering === s ? !filter.oplopend : true });

  const Kop = ({ s, children }: { s: Sortering; children: ReactNode }) => (
    <button
      type="button"
      onClick={() => sorteer(s)}
      className={cn('inline-flex items-center gap-1 hover:text-foreground', filter.sortering === s && 'font-semibold text-foreground')}
    >
      {children}
      {filter.sortering === s && (filter.oplopend ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
    </button>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Werkbalk */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <h1 className="mr-2 text-2xl font-semibold tracking-tight">Locaties</h1>
        <label className="relative order-last w-full sm:order-none sm:ml-auto sm:w-72">
          <span className="sr-only">Zoek locatie</span>
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            ref={zoekRef}
            value={filter.zoek}
            onChange={(e) => onFilter({ ...filter, zoek: e.target.value })}
            placeholder="Zoek locatie, project of plaats"
            className="h-9 pl-8 pr-8"
          />
          <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border bg-muted px-1.5 font-mono text-[11px] text-muted-foreground sm:block">/</kbd>
        </label>
        <div role="group" aria-label="Weergave" className="ml-auto flex rounded-lg border p-0.5 sm:ml-0">
          {([['lijst', List, 'Lijst'], ['kaart', KaartIcoon, 'Kaart']] as const).map(([w, Icoon, label]) => (
            <button
              key={w}
              type="button"
              aria-pressed={filter.weergave === w}
              onClick={() => onFilter({ ...filter, weergave: w })}
              className={cn('flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm', filter.weergave === w ? 'bg-muted font-medium' : 'text-muted-foreground hover:text-foreground')}
            >
              <Icoon className="h-4 w-4" aria-hidden />
              <span className="hidden md:inline">{label}</span>
            </button>
          ))}
        </div>
        {props.magBeheren && (
          <Button size="sm" onClick={props.onNieuw} className="h-9">
            <Plus className="mr-1.5 h-4 w-4" />
            <span className="hidden sm:inline">Nieuwe locatie</span>
            <span className="sm:hidden">Nieuw</span>
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label="Status" className="-mx-3 flex w-[calc(100%+1.5rem)] gap-1.5 overflow-x-auto px-3 [scrollbar-width:none] sm:mx-0 sm:w-auto sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
        {tabs.map((t) => {
          const aan = filter.tab === t.tab;
          return (
            <button
              key={t.tab}
              type="button"
              role="tab"
              aria-selected={aan}
              onClick={() => onFilter({ ...filter, tab: t.tab })}
              className={cn(
                'flex h-9 shrink-0 items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
                aan ? 'border-foreground bg-foreground text-background' : 'bg-background hover:bg-muted',
              )}
            >
              {STIP[t.tab] && <span className={cn('h-2 w-2 rounded-full', STIP[t.tab])} aria-hidden />}
              <span className="font-medium">{TAB_LABEL[t.tab]}</span>
              <span className={cn('tabular-nums', aan ? 'opacity-80' : 'text-muted-foreground')}>{t.aantal}</span>
            </button>
          );
        })}
      </div>

      {props.emsFout && (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Welke locaties energiemonitoring hebben kon niet worden gelezen, dus staat het kenmerk bij geen enkele locatie en kan er geen locatie worden verwijderd.
            {' '}<span className="font-mono text-xs">{props.emsFout}</span>
          </span>
        </div>
      )}

      {fout ? (
        <div className="rounded-xl border border-destructive/40 px-5 py-6 text-sm">
          <p className="font-medium text-destructive">Locaties konden niet geladen worden.</p>
          <p className="text-muted-foreground">{fout.message}</p>
        </div>
      ) : laden ? (
        <div className="flex flex-col gap-2 rounded-xl border p-4" aria-busy>
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-9 animate-pulse rounded bg-muted" />)}
        </div>
      ) : filter.weergave === 'kaart' ? (
        <div className="h-[calc(100vh-15rem)] min-h-[420px] overflow-hidden rounded-xl border" data-weergave="kaart">
          {props.kaart(zichtbaar)}
        </div>
      ) : zichtbaar.length === 0 ? (
        <div className="rounded-xl border px-5 py-10 text-center text-sm text-muted-foreground">
          {locaties.length === 0 ? 'Nog geen locaties.' : 'Geen locaties gevonden.'}{' '}
          {(filter.zoek || filter.tab !== 'alle') && locaties.length > 0 && (
            <button type="button" className="font-medium text-foreground underline" onClick={() => onFilter({ ...filter, zoek: '', tab: 'alle' })}>
              Toon alles
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-background">
          <div className="flex items-center border-b bg-muted/40 text-xs text-muted-foreground">
            <div className={cn('grid flex-1 items-center gap-3 py-2 pl-4 pr-2', KOLOMMEN)}>
              <Kop s="nummer">Nr</Kop>
              <Kop s="naam">Locatie</Kop>
              <span className="hidden sm:block">Type</span>
              <span className="hidden md:block"><Kop s="status">Status</Kop></span>
              <span className="hidden lg:block">Monitoring</span>
            </div>
            {props.magBeheren && <span className="w-[76px] shrink-0" />}
          </div>
          <ul className="divide-y">
            {zichtbaar.map((l, i) => {
              const plaats = plaatsUit(l.adres);
              const meta = [
                l.project ? [l.project.nummer, l.project.naam].filter(Boolean).join(' · ') : null,
                l.project?.organisatie && l.project.organisatie !== l.project.naam ? l.project.organisatie : null,
                plaats,
              ].filter(Boolean);
              const kenmerk = props.kenmerkVoor(l.id);
              return (
                <li key={l.id} data-site-rij={l.id} className={cn('group flex items-center', i === actief && 'bg-muted')}>
                  <Link
                    ref={(el) => (rijRefs.current[i] = el)}
                    to={`/crm/sites/${l.id}`}
                    onMouseEnter={() => setActief(i)}
                    aria-label={`${l.naam} openen`}
                    className={cn('grid flex-1 items-center gap-3 py-2.5 pl-4 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', KOLOMMEN)}
                  >
                    <span className="font-mono text-xs text-muted-foreground">{l.nummer ?? '—'}</span>
                    <span className="flex min-w-0 flex-col">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium">{l.naam}</span>
                        {kenmerk.soort === 'met-ems' && (
                          <Gauge className="h-3.5 w-3.5 shrink-0 text-emerald-700 lg:hidden" aria-label="Energiemonitoring" />
                        )}
                      </span>
                      {meta.length > 0 && <span className="truncate text-xs text-muted-foreground">{meta.join(' · ')}</span>}
                    </span>
                    <span className="hidden truncate text-muted-foreground sm:block">{siteTypeLabel(l.type)}</span>
                    <span className="hidden md:block">
                      <span className={cn('inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold', STATUS_KLEUR[l.status ?? ''] ?? 'bg-muted')}>
                        {siteStatusLabel(l.status)}
                      </span>
                    </span>
                    <span className="hidden min-w-0 lg:block"><Monitoring kenmerk={kenmerk} live={props.live.get(l.id)} /></span>
                  </Link>
                  {props.magBeheren && (
                    <span className="flex w-[76px] shrink-0 items-center justify-end gap-0.5 pr-2 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                      {l.sharepoint && (
                        <a href={l.sharepoint} target="_blank" rel="noreferrer" aria-label="SharePoint openen" className="hidden h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground xl:flex">
                          <Share className="h-4 w-4" />
                        </a>
                      )}
                      <Button variant="ghost" size="icon" aria-label="Bewerken" className="h-8 w-8 text-muted-foreground" onClick={() => props.onBewerk(l)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <VerwijderLocatie naam={l.naam} ems={kenmerk} onVerwijder={() => props.onVerwijder(l)} />
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="border-t px-4 py-2 text-xs text-muted-foreground">
            {zichtbaar.length} locatie{zichtbaar.length === 1 ? '' : 's'} · <kbd className="font-mono">/</kbd> zoeken · <kbd className="font-mono">↑ ↓</kbd> bladeren · <kbd className="font-mono">Enter</kbd> openen
          </p>
        </div>
      )}
    </div>
  );
}
